import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import styles from './postPage.module.css';
import { API_URL } from '../Utils/api';
import {
  FaArrowLeft, FaHeart, FaRegHeart, FaRegCommentDots,
  FaPaperPlane, FaClock, FaEllipsisV,
  FaRegBookmark, FaBookmark, FaTimes,
  FaQuestionCircle, FaExclamationTriangle, FaCalendar,
  FaBriefcase, FaNewspaper, FaLightbulb,
  FaMapMarkerAlt, FaUsers, FaCheckCircle, FaPlusCircle,
  FaLink, FaFlag, FaVolumeMute, FaUserTimes, FaEyeSlash, FaTrash,
  FaTag, FaImage
} from 'react-icons/fa';
import CloudinaryImage from '../Utils/CloudinaryImage';
import { useSinglePost } from '../hooks/useSinglePost';

/* ─── Config (identique à Posts.jsx) ──────────────────────── */
const TYPE_CONFIG = {
  announcement: { icon: FaClock,              color: '#1d9bf0', label: 'Annonce'     },
  alert:        { icon: FaExclamationTriangle, color: '#ff6b35', label: 'Alerte'      },
  event:        { icon: FaCalendar,            color: '#00ba7c', label: 'Événement'   },
  opportunity:  { icon: FaBriefcase,           color: '#1d9bf0', label: 'Opportunité' },
  news:         { icon: FaNewspaper,           color: '#f59e0b', label: 'Actualité'   },
  tip:          { icon: FaLightbulb,           color: '#06b6d4', label: 'Astuce'      },
};
const PRIORITY_CONFIG = {
  low:    { color: '#71767b', label: 'Basse'   },
  normal: { color: '#1d9bf0', label: 'Normale' },
  high:   { color: '#f59e0b', label: 'Haute'   },
  urgent: { color: '#ff6b35', label: 'Urgente' },
};
const CATEGORY_COLORS = {
  academic: '#1d9bf0', technical: '#1d9bf0', career: '#00ba7c',
  life: '#f59e0b', other: '#71767b', sport: '#00ba7c',
  culture: '#1d9bf0', education: '#1d9bf0', social: '#f59e0b', technology: '#06b6d4',
};

/* ─── Helpers ──────────────────────────────────────────────── */
const timeAgo = d => {
  if (!d) return '';
  const diff = Date.now() - new Date(d);
  const s = Math.floor(diff / 1000), m = Math.floor(diff / 60000),
        h = Math.floor(diff / 3600000), dd = Math.floor(diff / 86400000);
  if (s < 60)  return 'maintenant';
  if (m < 60)  return `${m}min`;
  if (h < 24)  return `${h}h`;
  if (dd < 7)  return `${dd}j`;
  if (dd < 30) return `${Math.floor(dd / 7)} sem`;
  const mo = Math.floor(dd / 30);
  return mo < 12 ? `${mo} mois` : `${Math.floor(dd / 365)} an${Math.floor(dd / 365) > 1 ? 's' : ''}`;
};

const fmtEvent = d => new Date(d).toLocaleDateString('fr-FR', {
  day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
});

const formatCommentText = (text) => {
  if (!text) return '';
  const parts = text.split(/(@\w+)/g);
  return parts.map((part, i) => 
    part.startsWith('@') ? 
    <span key={i} style={{ color: '#2563eb', fontWeight: 600 }}>{part}</span> : 
    part
  );
};

/* ─── Options dropdown (3 points) ─────────────────────────── */
const PostOptions = ({ post, userId, onClose, anchorRef }) => {
  const isOwner = post?.userId === userId || post?.userId?.toString() === userId?.toString();
  const ref = useRef(null);

  useEffect(() => {
    const h = e => {
      if (ref.current && !ref.current.contains(e.target) &&
          anchorRef?.current && !anchorRef.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [onClose, anchorRef]);

  useEffect(() => {
    const h = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    onClose();
  };

  const handleReport = () => {
    try {
      const existing = JSON.parse(localStorage.getItem('admin_reported') || '[]');
      if (!existing.some(r => r.postId === post._id?.toString())) {
        existing.push({ postId: post._id?.toString(), reportedAt: new Date().toISOString(), reportedBy: userId, reason: 'Contenu inapproprié' });
        localStorage.setItem('admin_reported', JSON.stringify(existing));
      }
    } catch {}
    onClose();
  };

  const Item = ({ icon: Icon, label, onClick, danger = false }) => (
    <button className={`${styles.optionItem} ${danger ? styles.optionDanger : ''}`} onClick={onClick}>
      <Icon className={styles.optionIcon} /><span>{label}</span>
    </button>
  );

  return (
    <div className={styles.optionsDropdown} ref={ref} role="menu">
      <Item icon={FaLink}     label="Copier le lien"           onClick={handleCopyLink} />

      {isOwner ? (
        <>
          <div className={styles.optionDivider}/>
          <Item icon={FaTrash} label="Supprimer" onClick={onClose} danger />
        </>
      ) : (
        <>
          <div className={styles.optionDivider}/>
          <Item icon={FaFlag}       label="Signaler"           onClick={handleReport} danger />
        </>
      )}
    </div>
  );
};

/* ─── Main component ────────────────────────────────────────── */
const PostPage = () => {
  const { id }   = useParams();
  const navigate = useNavigate();

  const [post,             setPost]             = useState(null);
  const [liked,            setLiked]            = useState(false);
  const [saved,            setSaved]            = useState(false);
  const [likeCount,        setLikeCount]        = useState(0);
  const [comments,         setComments]         = useState([]);
  const [commentText,      setCommentText]      = useState('');
  const [isSubmitting,     setIsSubmitting]     = useState(false);
  const [showAllComments,  setShowAllComments]  = useState(false);
  const [showComments,     setShowComments]     = useState(true);
  const [optionsOpen,      setOptionsOpen]      = useState(false);
  const [argumentSide,     setArgumentSide]     = useState('for'); // 'for' or 'against'
  const [postImage,        setPostImage]        = useState(null);
  const [imagePreview,     setImagePreview]     = useState(null);

  const commentInputRef = useRef(null);
  const optionsBtnRef   = useRef(null);
  const fileInputRef    = useRef(null);

  const token  = useMemo(() => localStorage.getItem('token'),  []);
  const userId = useMemo(() => localStorage.getItem('userId'), []);

  const { data: fetchedPost, isLoading: loading, isError, refetch } = useSinglePost(token, id);
  const error = isError ? 'Impossible de charger le post' : null;

  useEffect(() => {
    if (fetchedPost) {
      setPost(fetchedPost);
      setLikeCount(fetchedPost.likesCount || 0);
      // Note: Sans tracking de qui a liké dans le nouveau modèle Post,
      // on peut simuler ou renvoyer 'liked' depuis le serveur. 
      // Pour l'instant, on se base sur la réponse du serveur si elle l'inclut.
      setLiked(fetchedPost.likedByMe || false);
      setSaved(false); // Champ favoris absent du nouveau modèle
      setComments(fetchedPost.postComment || []);
    }
  }, [fetchedPost, userId]);

  const type = post?.postType || post?.type || post?.post?.type;


  /* like */
  const handleLike = useCallback(async () => {
    const prev = { liked, likeCount };
    setLiked(v => !v);
    setLikeCount(v => liked ? v - 1 : v + 1);
    try {
      await axios.put(`${API_URL}/post/like`, { postId: id, authorId: post.userId },
        { headers: { Authorization: `Bearer ${token}` } });
      refetch();
    } catch { setLiked(prev.liked); setLikeCount(prev.likeCount); }
  }, [id, token, liked, likeCount, post, refetch]);

  /* save */
  const handleSave = useCallback(async () => {
    const prev = saved;
    setSaved(v => !v);
    try {
      await axios.put(`${API_URL}/post/favoris`, { postId: id },
        { headers: { Authorization: `Bearer${token}` } });
    } catch { setSaved(prev); }
  }, [id, token, saved]);

  /* share */
  const handleShare = useCallback(async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: 'Publication', text: post?.postText || '', url }); return; }
      catch (e) { if (e.name === 'AbortError') return; }
    }
    navigator.clipboard.writeText(url);
    try {
      await axios.put(`${API_URL}/post/sharing`, { currentPostId: id, userId },
        { headers: { Authorization: `Bearer ${token}` } });
    } catch {}
  }, [id, token, userId, post]);

  const handleImgChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setPostImage(reader.result);
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setPostImage(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  /* like argument */
  const handleLikeArgument = useCallback(async (argId) => {
    // Optimistic Update
    setComments(prev => prev.map(c => {
      if (c._id === argId) {
        const alreadyLiked = c.votedBy?.includes(userId);
        return {
          ...c,
          votedBy: alreadyLiked ? c.votedBy.filter(id => id !== userId) : [...(c.votedBy || []), userId],
          votes: alreadyLiked ? (c.votes || 0) - 1 : (c.votes || 0) + 1
        };
      }
      return c;
    }));

    try {
      await axios.put(`${API_URL}/argument/like`, { argumentId: argId },
        { headers: { Authorization: `Bearer${token}` } });
      refetch();
    } catch {
      // Rollback (simplifié : on laisse comme ça ou on refresh tout)
      console.error("Erreur vote argument");
    }
  }, [comments, token, userId, id, refetch]);

  /* reply to argument */
  const handleReply = useCallback((username) => {
    setCommentText(`@${username} `);
    commentInputRef.current?.focus();
  }, []);

  /* comment */
  const handleComment = useCallback(async () => {
    if ((!commentText.trim() && !postImage) || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const { data } = await axios.put(`${API_URL}/post/comment`,
        { 
          commentary: commentText.trim(), 
          currentPostId: id, 
          side: argumentSide,
          postPicture: postImage
        },
        { headers: { Authorization: `Bearer ${token}` } });
      setComments(data.commentaires || []);
      setCommentText('');
      setPostImage(null);
      setImagePreview(null);
      setShowComments(true);
    } catch (e) { console.error(e); }
    finally { setIsSubmitting(false); }
  }, [commentText, postImage, isSubmitting, id, userId, token, post, argumentSide]);

  /* ─── Rendu du contenu selon type ─────────────────────────── */
  const renderContent = useCallback(() => {
    if (!post) return null;
    const type = post.postType || post.type || post.post?.type;
    const d    = post.post || post || {};


    /* Débat */
    if (type === 'debate' || type === 'question') {
      return (
        <div className={styles.specialContent}>
          <div className={styles.badgeRow}>
            <span className={styles.typeBadge} style={{ background: 'rgba(37, 99, 235, 0.1)', color: '#2563eb' }}>
              <FaQuestionCircle /> Débat
            </span>
            {post.category && (
              <span className={styles.categoryBadge} style={{ background: CATEGORY_COLORS[post.category] + '18', color: CATEGORY_COLORS[post.category] || '#1d9bf0' }}>
                {post.category}
              </span>
            )}
          </div>
          <h2 className={styles.specialTitle}>{post.title}</h2>
          {post.postText && <p className={styles.specialBody}>{post.postText}</p>}
          
          {post.postPicture && (
            <div className={styles.imageWrapper}>
              <img src={post.postPicture} alt="post" />
            </div>
          )}

          {post.tags?.length > 0 && (
            <div className={styles.tagsRow}>
              {post.tags.map((t, i) => <span key={i} className={styles.tag}>#{t}</span>)}
            </div>
          )}
        </div>
      );
    }


    /* Info */
    if (post.postType === 'info') {
      const cfg  = TYPE_CONFIG[post.category] || TYPE_CONFIG.announcement;
      const pri  = PRIORITY_CONFIG[post.priority] || PRIORITY_CONFIG.normal;
      const Icon = cfg.icon;
      return (
        <div className={styles.specialContent}>
          <div className={styles.badgeRow}>
            <span className={styles.typeBadge} style={{ background: cfg.color + '18', color: cfg.color }}>
              <Icon /> {cfg.label}
            </span>
            {post.priority && post.priority !== 'normal' && (
              <span className={styles.priorityBadge} style={{ background: pri.color }}>{pri.label}</span>
            )}
          </div>
          <h2 className={styles.specialTitle}>{post.title}</h2>
          <p className={styles.specialBody}>{post.postText}</p>
          {post.postPicture && <div className={styles.imageWrapper}><img src={post.postPicture} alt=""/></div>}
          {post.link && (
            <a href={post.link} target="_blank" rel="noopener noreferrer" className={styles.linkButton}>
              🔗 En savoir plus
            </a>
          )}
        </div>
      );
    }

    /* Event */
    if (type === 'event') return (
      <div className={styles.specialContent}>
        <div className={styles.badgeRow}>
          <span className={styles.typeBadge} style={{ background: '#00ba7c18', color: '#00ba7c' }}>
            <FaCalendar /> Événement
          </span>
          {post.category && (
            <span className={styles.categoryBadge} style={{ background: CATEGORY_COLORS[post.category] + '18', color: CATEGORY_COLORS[post.category] }}>
              {post.category}
            </span>
          )}
        </div>
        <h2 className={styles.specialTitle}>{post.title}</h2>
        <p className={styles.specialBody}>{post.postText}</p>
        {post.postPicture && <div className={styles.imageWrapper}><img src={post.postPicture} alt=""/></div>}
        <div className={styles.eventDetails}>
          {d.startDate && (
            <div className={styles.eventDetailRow}>
              <FaClock /><span>{fmtEvent(d.startDate)}{d.endDate && ` → ${fmtEvent(d.endDate)}`}</span>
            </div>
          )}
          {d.location  && <div className={styles.eventDetailRow}><FaMapMarkerAlt /><span>{d.location}</span></div>}
          {d.maxParticipants && (
            <div className={styles.eventDetailRow}>
              <FaUsers /><span>{d.participants?.length || 0}/{d.maxParticipants} participants</span>
            </div>
          )}
        </div>
        <button className={`${styles.participateBtn} ${d.participants?.includes(userId) ? styles.participating : ''}`}>
          {d.participants?.includes(userId) ? <><FaCheckCircle /> Inscrit</> : <><FaPlusCircle /> Participer</>}
        </button>
      </div>
    );

    /* Regular */
    return (
      <>
        {post.postPicture && (
          <div className={styles.imageWrapper}>
            <img src={post.postPicture} alt="post" loading="lazy"/>
            {post.title && <div className={styles.articleOverlay}><h2>{post.title}</h2></div>}
          </div>
        )}
        {post.postText && <p className={styles.postText}>{post.postText}</p>}
      </>
    );
  }, [post, userId]);

  /* ─── Loading ─────────────────────────────────────────────── */
  if (loading) return (
    <div className={styles.page}>
      <div className={styles.loadingState}>
        {/* Skeleton avatar + lines */}
        <div className={styles.skeletonPost}>
          <div className={styles.skeletonAvatar}/>
          <div className={styles.skeletonLines}>
            <div className={styles.skeletonLine} style={{width:'40%'}}/>
            <div className={styles.skeletonLine} style={{width:'20%'}}/>
          </div>
        </div>
        <div className={styles.skeletonLine} style={{width:'100%', height:200, borderRadius:12}}/>
        <div className={styles.skeletonLine} style={{width:'80%'}}/>
        <div className={styles.skeletonLine} style={{width:'60%'}}/>
      </div>
    </div>
  );

  /* ─── Error ───────────────────────────────────────────────── */
  if (error || !post) return (
    <div className={styles.page}>
      <div className={styles.errorState}>
        <FaRegCommentDots size={48} opacity={0.3}/>
        <h3>{error || 'Post introuvable'}</h3>
        <p>Ce post n'existe pas ou a été supprimé</p>
        <button className={styles.errorBack} onClick={() => navigate(-1)}>
          <FaArrowLeft /> Retour
        </button>
      </div>
    </div>
  );

  const displayedComments = showAllComments ? comments : comments.slice(0, 5);

  return (
    <div className={styles.page}>

      {/* ── Topbar ── */}
      <div className={styles.topbar}>
        <button className={styles.backBtn} onClick={() => navigate(-1)} aria-label="Retour">
          <FaArrowLeft />
        </button>
        <span className={styles.topbarTitle}>Publication</span>
        <div className={styles.moreWrap}>
          <button
            ref={optionsBtnRef}
            className={`${styles.moreBtn} ${optionsOpen ? styles.moreBtnActive : ''}`}
            onClick={() => setOptionsOpen(v => !v)}
            aria-label="Options"
          >
            <FaEllipsisV />
          </button>
          {optionsOpen && (
            <PostOptions
              post={post}
              userId={userId}
              onClose={() => setOptionsOpen(false)}
              anchorRef={optionsBtnRef}
            />
          )}
        </div>
      </div>

      {/* ── Article ── */}
      <article className={styles.article}>

        {/* Author row — identique au composant Posts */}
        <div className={styles.authorRow}>
          <div className={styles.avatarCol}>
            {post.userPP || post.pp ? (
              <img 
                src={post.userPP || post.pp} 
                alt={post.username} 
                className={styles.avatar}
              />
            ) : (
              <div className={styles.avatarPlaceholder}>
                {post.username?.charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          <div className={styles.authorInfo}>
            <div className={styles.authorName}>
              <p onClick={() => navigate(`/profile/${post.userId}`)}>{post.username}</p>
              <span className={styles.postTime}>{timeAgo(post.createdAt)}</span>
            </div>
          </div>
        </div>

        {/* Contenu */}
        <div className={styles.body}>
          {renderContent()}
        </div>

        {/* ── Barre d'actions — même design que Posts ── */}
        <div className={styles.actions}>
          <div className={styles.leftActions}>
            {/* Like */}
            <button
              className={`${styles.actionBtn} ${liked ? styles.liked : ''}`}
              onClick={handleLike}
            >
              {liked ? <FaHeart className={styles.iconLiked}/> : <FaRegHeart/>}
              {likeCount > 0 && <span className={styles.count}>{likeCount}</span>}
            </button>

            {/* Comment toggle */}
            <button
              className={`${styles.actionBtn} ${showComments ? styles.commentActive : ''}`}
              onClick={() => { setShowComments(v => !v); if (!showComments) setTimeout(() => commentInputRef.current?.focus(), 100); }}
            >
              <FaRegCommentDots/>
              {comments.length > 0 && <span className={styles.count}>{comments.length}</span>}
            </button>

            {/* Share */}
            <button className={styles.actionBtn} onClick={handleShare}>
              <FaPaperPlane/>
              {post.sharing?.length > 0 && <span className={styles.count}>{post.sharing.length}</span>}
            </button>
          </div>

          {/* Save */}
          <button className={`${styles.actionBtn} ${saved ? styles.saved : ''}`} onClick={handleSave}>
            {saved ? <FaBookmark/> : <FaRegBookmark/>}
          </button>
        </div>

        {/* ── Séparateur likes ── */}
        {likeCount > 0 && (
          <p className={styles.likesSummary}>
            <strong>{likeCount}</strong> {likeCount === 1 ? "mention J'aime" : "mentions J'aime"}
          </p>
        )}

        {/* ── Section arguments (Twitter Style) ── */}
        {showComments && (
          <div className={styles.commentsSection}>
            {comments.length > 0 ? (
              <>
                {displayedComments.map((c, i) => (
                  <div key={i} className={`${styles.argumentTwitter} ${c.side === 'against' ? styles.argAgainstBorder : ''}`}>
                    {c.userPP || c.pp ? (
                      <img 
                        src={c.userPP || c.pp} 
                        alt={c.username} 
                        className={styles.argAvatar}
                        onClick={() => navigate(`/profile/${c.userId}`)}
                      />
                    ) : (
                      <div 
                        className={styles.avatarPlaceholder}
                        onClick={() => navigate(`/profile/${c.userId}`)}
                        style={{ width: 40, height: 40, fontSize: 18 }}
                      >
                        {c.username?.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className={styles.argContent}>
                      <div className={styles.argHeader}>
                        <span className={styles.argUser} onClick={() => navigate(`/profile/${c.userId}`)}>{c.username}</span>
                        <span className={styles.argTime}>{timeAgo(c.createdAt)}</span>
                        {type === 'debate' && !c.commentary?.trim().startsWith('@') && (
                          c.side === 'for' ? 
                          <span className={styles.argBadgeFor}>POUR</span> : 
                          <span className={styles.argBadgeAgainst}>CONTRE</span>
                        )}

                      </div>
                      <p className={styles.argText}>{formatCommentText(c.commentary)}</p>
                      {c.imageUrl && (
                        <div className={styles.argImageWrap}>
                          <img src={c.imageUrl} alt="argument" loading="lazy" />
                        </div>
                      )}
                      <div className={styles.argActions}>
                        <button 
                          className={`${styles.argActionBtn} ${c.votedBy?.includes(userId) ? styles.argLiked : ''}`}
                          onClick={() => handleLikeArgument(c._id)}
                        >
                          {c.votedBy?.includes(userId) ? <FaHeart color="#f4212e" /> : <FaRegHeart />}
                          <span>{c.votes || 0}</span>
                        </button>
                        <button 
                          className={styles.argActionBtn}
                          onClick={() => handleReply(c.username)}
                        >
                          <FaRegCommentDots />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                {comments.length > 5 && !showAllComments && (
                  <button
                    className={styles.viewAllBtn}
                    onClick={() => setShowAllComments(true)}
                  >
                    Voir les {comments.length} commentaires
                  </button>
                )}
              </>
            ) : (
              <div className={styles.noComments}>
                <FaRegCommentDots/>
                <span>Aucun commentaire</span>
              </div>
            )}
          </div>
        )}

        {/* ── Input commentaire ── */}
        <div className={styles.argumentInputCard}>
          {type !== 'sale' && (
            <div className={styles.sideSelector}>
              <button 
                className={`${styles.sideButton} ${argumentSide === 'for' ? styles.sideForActive : ''}`}
                onClick={() => setArgumentSide('for')}
              >👍 Pour</button>
              <button 
                className={`${styles.sideButton} ${argumentSide === 'against' ? styles.sideAgainstActive : ''}`}
                onClick={() => setArgumentSide('against')}
              >👎 Contre</button>
            </div>
          )}

          {imagePreview && (
            <div className={styles.inputImagePreview}>
              <img src={imagePreview} alt="Aperçu" translate="no" />
              <button className={styles.removePreviewBtn} onClick={handleRemoveImage}><FaTimes /></button>
            </div>
          )}
          <div className={styles.commentInputRow}>
            <button className={styles.addImageBtn} onClick={() => fileInputRef.current?.click()} type="button">
              <FaImage />
            </button>
            <input 
              ref={fileInputRef}
              type="file" 
              accept="image/*" 
              onChange={handleImgChange} 
              style={{ display: 'none' }} 
            />
            <input
              ref={commentInputRef}
              type="text"
              placeholder={type === 'sale' ? "Posez une question sur cet article..." : `Donnez votre argument ${argumentSide === 'for' ? 'POUR' : 'CONTRE'}...`}
              value={commentText}
              onChange={e => setCommentText(e.target.value)}
              onFocus={() => setShowComments(true)}
              onKeyPress={e => { if (e.key === 'Enter') { e.preventDefault(); handleComment(); }}}
              className={styles.commentInput}
            />
            {(commentText.trim() || postImage) && (
              <button className={styles.sendCommentBtn} onClick={handleComment} disabled={isSubmitting}>
                {isSubmitting ? '…' : (type === 'sale' ? 'Envoyer' : 'Argumenter')}
              </button>
            )}

          </div>
        </div>

      </article>
    </div>
  );
};

export default PostPage;